import { useCallback, useMemo } from 'react';
import { RackAlongsideExtension } from '@thive/fddm-types';

import { GapHorizontalIcon } from '@/assets/icons';
import { SelectInput } from '@common/components/inputs';
import { PropertiesLabel } from '@/modules/common/components/PropertiesLabel';
import { useReadSelectedAreas } from '@/modules/floorplanDesign/hooks';
import { areasToFlatCollection, isRackingArea } from '@/modules/floorplanDesign/helpers';
import { FddmServiceFactory } from '@/modules/canvas/services/Floorplan/FddmServiceFactory';
import { NumberInput } from '@/modules/common/components/inputs/components/NumberInput';
import { NumUtils } from '@/modules/common/utils';

const POI_DIRECTION_OPTIONS: {
  label: string;
  value: RackAlongsideExtension['poiDirection'];
}[] = [
  {
    label: 'Left to right',
    value: 'LEFT_TO_RIGHT',
  },
  {
    label: 'Right to left',
    value: 'RIGHT_TO_LEFT',
  },
  {
    label: 'Both directions',
    value: 'BOTH_DIRECTIONS',
  },
];

function AlongsideExtension() {
  const selectedAreas = useReadSelectedAreas();
  const fddmSvc = useMemo(() => FddmServiceFactory.getInstance().createFddmService(), []);

  const alongsideExtension = useMemo(() => {
    const areas = areasToFlatCollection(selectedAreas);
    if (areas.length !== 1) return;
    const area = areas[0];
    if (!isRackingArea(area)) return;

    const { alongsideExtension } = area;
    return alongsideExtension;
  }, [selectedAreas]);

  const onAisleWidthChange = useCallback(
    (newValueMm: number) => {
      const areas = areasToFlatCollection(selectedAreas);
      if (areas.length !== 1) return;
      const area = areas[0];
      if (!isRackingArea(area)) return;

      if (!newValueMm || Number.isNaN(newValueMm)) return;

      const newValue = NumUtils.mmToMeters(newValueMm);

      fddmSvc.setPreference('rackingArea.alongsideExtension.aisleWidth', newValue);
      fddmSvc.updateArea({
        ...area,
        alongsideExtension: {
          aisleWidth: newValue,
          poiDirection: area.alongsideExtension?.poiDirection ?? 'BOTH_DIRECTIONS',
        },
      });
    },
    [fddmSvc, selectedAreas],
  );

  const onPoiDirectionChange = useCallback(
    (newValue: RackAlongsideExtension['poiDirection']) => {
      const areas = areasToFlatCollection(selectedAreas);
      if (areas.length !== 1) return;
      const area = areas[0];
      if (!isRackingArea(area)) return;

      fddmSvc.setPreference('rackingArea.alongsideExtension.poiDirection', newValue);
      fddmSvc.updateArea({
        ...area,
        alongsideExtension: {
          aisleWidth: area.alongsideExtension?.aisleWidth ?? 0,
          poiDirection: newValue,
        },
      });
    },
    [fddmSvc, selectedAreas],
  );

  if (!alongsideExtension) return null;

  return (
    <>
      <PropertiesLabel i18nkey='interface:properties.area.aisle_width'>
        <NumberInput
          value={NumUtils.metersToMmRounded(alongsideExtension.aisleWidth)}
          unit='mm'
          prefix={<GapHorizontalIcon />}
          sx={{ width: '100%' }}
          minValue={0}
          onChange={onAisleWidthChange}
        />
      </PropertiesLabel>
      <PropertiesLabel i18nkey='interface:properties.area.poi_direction'>
        <SelectInput
          options={POI_DIRECTION_OPTIONS}
          onChange={onPoiDirectionChange}
          value={alongsideExtension.poiDirection}
        />
      </PropertiesLabel>
    </>
  );
}

export default AlongsideExtension;
